import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import NavigationHeader from '../../components/ui/NavigationHeader';
import CancelSubscriptionModal from './components/CancelSubscriptionModal';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import { PLANS, PLAN_ORDER, PLAN_SLUGS, SHOW_LAUNCH_PROMO, getPlanConfig } from '../../config/plans';
import { stripeService } from '../../services/stripe';
import { useAuth } from '../../contexts/AuthContext';

const promoMessage =
  'Offre de lancement : utilisez votre code promo au paiement pour obtenir un tarif préférentiel sur le plan Pro.';

const SubscriptionPricing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, userProfile, refreshProfile } = useAuth();

  const currentPlan = stripeService.normalizeTier(userProfile?.subscription_tier);
  const [loadingPlan, setLoadingPlan] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [isCancelledAtPeriodEnd, setIsCancelledAtPeriodEnd] = useState(false);
  const [nextBillingDate, setNextBillingDate] = useState(null);

  const currentPlanConfig = getPlanConfig(currentPlan);

  useEffect(() => {
    if (searchParams.get('checkout') === 'cancelled') {
      setInfo('Paiement annulé. Aucun montant n’a été facturé; vous pouvez choisir un plan quand vous voulez.');
    }
  }, [searchParams]);

  useEffect(() => {
    const check = async () => {
      if (!user?.id) return;
      try {
        const { data: subscription } = await stripeService.getUserSubscription(user.id);
        setIsCancelledAtPeriodEnd(!!subscription?.cancel_at_period_end);
        setNextBillingDate(
          subscription?.current_period_end
            ? new Date(subscription.current_period_end).toLocaleDateString('fr-CA')
            : null
        );
      } catch {
        /* silent */
      }
    };
    check();
  }, [user?.id]);

  const handleTrial = async () => {
    setError('');
    if (!user) {
      sessionStorage.setItem('intended_tier', PLAN_SLUGS.TRIAL);
      navigate('/register');
      return;
    }
    setLoadingPlan(PLAN_SLUGS.TRIAL);
    try {
      if (userProfile?.subscription_tier !== PLAN_SLUGS.TRIAL) {
        const { error: trialError } = await stripeService.assignTrialPlan(user.id);
        if (trialError) throw new Error(trialError);
        await refreshProfile?.();
      }
      navigate('/main-dashboard');
    } catch (e) {
      setError(e?.message || "Impossible d'activer l'essai gratuit.");
    } finally {
      setLoadingPlan('');
    }
  };

  const startCheckout = async (planSlug) => {
    setError('');
    const normalized = stripeService.normalizeTier(planSlug);
    if (![PLAN_SLUGS.ANALYSIS, PLAN_SLUGS.PRO].includes(normalized)) {
      setError('Plan invalide pour le paiement.');
      return;
    }
    if (!user) {
      sessionStorage.setItem('intended_tier', normalized);
      navigate('/login');
      return;
    }
    setLoadingPlan(normalized);
    try {
      const { data, error: checkoutError } = await stripeService.startCheckout(
        normalized,
        user.id,
        user.email
      );
      if (checkoutError) throw new Error(checkoutError);
      if (!data?.url) throw new Error('URL de paiement non disponible');
      window.location.href = data.url;
    } catch (e) {
      setError(e?.message || 'Erreur lors de la création de la session de paiement.');
      setLoadingPlan('');
    }
  };

  const handlePlanSelect = (planSlug) => {
    if (planSlug === PLAN_SLUGS.TRIAL) return handleTrial();
    return startCheckout(planSlug);
  };

  const handleCancelSubscription = async (cancellationData) => {
    if (!user?.id) {
      setError('Connectez-vous pour annuler.');
      return;
    }
    setCancelling(true);
    setError('');
    try {
      const { error: cancelError } = await stripeService.cancelSubscription(user.id, cancellationData);
      if (cancelError) throw new Error(cancelError);
      setShowCancelModal(false);
      await refreshProfile?.();
      navigate('/main-dashboard');
    } catch (e) {
      setError(e?.message || "Impossible d'annuler l'abonnement.");
    } finally {
      setCancelling(false);
    }
  };

  const comparisonRows = useMemo(() => ([
    { name: 'Analyses immobilières', trial: '3 au total', analysis: true, pro: 'Illimitées' },
    { name: 'Calcul de rentabilité de base', trial: true, analysis: true, pro: true },
    { name: 'Sauvegarde des immeubles', trial: 'Limitée', analysis: true, pro: true },
    { name: 'Calcul de cashflow', trial: false, analysis: true, pro: true },
    { name: 'Mise de fonds estimée', trial: false, analysis: true, pro: true },
    { name: 'Ratios de rentabilité', trial: false, analysis: true, pro: true },
    { name: 'Scénarios de taux d’intérêt', trial: false, analysis: true, pro: true },
    { name: 'Comparaison de scénarios', trial: false, analysis: false, pro: true },
    { name: 'Rapports détaillés', trial: false, analysis: false, pro: true },
    { name: 'Projections avancées', trial: false, analysis: false, pro: true },
    { name: 'Tableau de bord investisseur', trial: false, analysis: false, pro: true },
  ]), []);

  const renderCompareCell = (value) => {
    if (value === true) return <span className="text-emerald-600 font-bold">✓</span>;
    if (!value) return <span className="text-muted-foreground">—</span>;
    return <span>{value}</span>;
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16">
        <section className="text-center mb-10">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs text-muted-foreground mb-4">
            <Icon name="ShieldCheck" size={14} className="text-primary" />
            Plans simples, paiement sécurisé par Stripe
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-foreground" style={{ fontFamily: 'var(--font-heading)' }}>
            Choisissez le plan ImmoIQ qui suit votre rythme
          </h1>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Commencez avec 3 analyses gratuites, puis passez à Analyse ou Pro selon votre volume et vos besoins de projection.
          </p>
        </section>

        {info && (
          <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {info}
          </div>
        )}
        {error && (
          <div className="mb-6 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="mb-8 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 flex items-start gap-3">
          <Icon name="Gift" size={18} className="text-primary mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-foreground">Offre lancement / GP3R</p>
            <p className="text-sm text-muted-foreground">{promoMessage}</p>
            {SHOW_LAUNCH_PROMO && (
              <p className="text-xs text-primary mt-1">
                Les codes promo sont saisis dans Stripe Checkout; le rabais réel n’est pas calculé dans ImmoIQ.
              </p>
            )}
          </div>
        </div>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10" aria-label="Plans ImmoIQ">
          {PLAN_ORDER.map((slug) => {
            const plan = PLANS[slug];
            const isCurrentPlan = currentPlan === slug;
            const isLoading = loadingPlan === slug;
            return (
              <div
                key={slug}
                className={`relative rounded-2xl border bg-card p-6 flex flex-col ${plan.recommended ? 'border-primary shadow-lg shadow-primary/10' : 'border-border'}`}
              >
                {plan.recommended && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary text-primary-foreground px-3 py-1 text-xs font-semibold">
                    Recommandé
                  </div>
                )}
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-bold text-foreground">{plan.name}</h2>
                    <p className="text-sm text-muted-foreground mt-1">{plan.description}</p>
                  </div>
                  {isCurrentPlan && (
                    <span className="rounded-full border border-primary/30 bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
                      Votre plan
                    </span>
                  )}
                </div>

                <div className="my-6">
                  <span className="text-4xl font-bold text-foreground">{plan.priceLabel}</span>
                </div>

                <ul className="space-y-3 flex-1">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-foreground">
                      <Icon name="Check" size={17} className="text-emerald-600 mt-0.5 shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  fullWidth
                  className="mt-6"
                  variant={plan.recommended ? 'default' : 'outline'}
                  disabled={isLoading || (isCurrentPlan && slug !== PLAN_SLUGS.TRIAL)}
                  onClick={() => handlePlanSelect(slug)}
                  data-testid={`select-plan-${slug}`}
                >
                  {isLoading ? 'Chargement...' : isCurrentPlan && slug !== PLAN_SLUGS.TRIAL ? 'Plan actif' : plan.cta}
                </Button>

                {isCurrentPlan && slug !== PLAN_SLUGS.TRIAL && !isCancelledAtPeriodEnd && (
                  <button
                    type="button"
                    className="mt-3 text-sm text-destructive hover:underline"
                    onClick={() => setShowCancelModal(true)}
                    disabled={cancelling}
                  >
                    Se désabonner
                  </button>
                )}
              </div>
            );
          })}
        </section>

        {nextBillingDate && currentPlan !== PLAN_SLUGS.TRIAL && (
          <div className="mb-8 rounded-xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
            <strong className="text-foreground">Plan {currentPlanConfig.name} actif.</strong>{' '}
            {isCancelledAtPeriodEnd
              ? `Annulation prévue le ${nextBillingDate}. Vous gardez l’accès jusqu’à cette date.`
              : `Prochaine facturation : ${nextBillingDate}.`}
          </div>
        )}

        <section className="overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Fonctionnalité</th>
                {PLAN_ORDER.map((slug) => (
                  <th key={slug} className="text-center px-4 py-3 font-medium">{PLANS[slug].name}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {comparisonRows.map((row) => (
                <tr key={row.name}>
                  <td className="px-4 py-3 text-foreground">{row.name}</td>
                  {PLAN_ORDER.map((slug) => (
                    <td key={slug} className="px-4 py-3 text-center">
                      {renderCompareCell(row[slug])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </main>

      <CancelSubscriptionModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelSubscription}
        cancelling={cancelling}
      />
    </div>
  );
};

export default SubscriptionPricing;
